import { Obj } from 'app/tools/general.types';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

type TitleObj = {
  title?: string | null;
  altHeader?: React.ReactNode;
  scrollHeader?: boolean;
};
type PageTitleHook = [TitleObj, (titleObj: TitleObj) => void];
const PageTitleContext = React.createContext<PageTitleHook>([
  {},
  () => {},
]);

export const usePageTitle = (): TitleObj =>
  useContext(PageTitleContext)[0];

export const useSetPageTitle = (
  nullableInput?: string | TitleObj | null,
  useLayout?: 'useLayout'
): void => {
  const setPageTitle = useContext(PageTitleContext)[1];
  const input = nullableInput || {};

  const title = typeof input === 'string' ? input : input.title;
  const altHeader = typeof input !== 'string' && input.altHeader;
  const scrollHeader =
    typeof input !== 'string' && input.scrollHeader;

  const titleObj = useMemo(
    () => ({
      title,
      altHeader,
      scrollHeader,
    }),
    [altHeader, title, scrollHeader]
  );

  useLayoutEffect(() => {
    if (useLayout) setPageTitle(titleObj);
  }, [setPageTitle, titleObj, useLayout]);

  useEffect(() => {
    if (!useLayout) setPageTitle(titleObj);
  }, [setPageTitle, titleObj, useLayout]);
};

type Props = {
  children: React.ReactNode;
};

export function PageTitleProvider({ children }: Props): JSX.Element {
  const location = useLocation();
  const pathId = location.pathname + location.search;

  const [titleStore, setTitleStore] = useState<Obj<TitleObj>>({
    [pathId]: {},
  });

  const setTitle = useCallback(
    (titleObj: TitleObj) =>
      setTitleStore(s => ({
        ...s,
        [pathId]: { ...s[pathId], ...titleObj },
      })),
    [pathId]
  );

  return (
    <PageTitleContext.Provider
      value={[titleStore[pathId] || {}, setTitle]}
    >
      {children}
    </PageTitleContext.Provider>
  );
}
